/**
 * Design tokens, in TypeScript because ECharts needs values, not CSS variables.
 *
 * `src/styles/tokens.css` mirrors these as custom properties for the shell.
 * This file is the source of truth; see `docs/design-contract.md` for the
 * rationale, the accessibility rules and the palette validator output.
 */

export const surface = {
	/** Page background, behind every panel. */
	page: "#0B0C10",
	/** Panel and chart surface. Every series colour below was validated against this. */
	panel: "#0E0F14",
	/** Raised elements: header strip, sidebar rows, evidence cards. */
	raised: "#14161D",
	/** Hover/selected row. */
	hover: "#1B1E27",
	border: "#232734",
	grid: "#1A1D26",
} as const;

export const ink = {
	primary: "#E6E9F2",
	secondary: "#98A0B4",
	muted: "#787F94",
	/** Text on a brand-filled surface. */
	inverse: "#0B0C10",
} as const;

/** Brand accents. UI chrome only — never a series colour, so identity never drifts. */
export const brand = {
	purple: "#A46BF2",
	green: "#12A585",
	focus: "#C9A5FF",
} as const;

/**
 * Reserved market status pair. Direction, not identity.
 *
 * Green/red cannot pass a deuteranopia check — that is inherent to the
 * convention this product is read in — so every mark that uses it carries a
 * second encoding: candle bodies are hollow up / filled down, net-value bars
 * sit above or below the zero baseline, and profile zones are labelled with
 * their sign in the evidence panel.
 */
export const market = {
	up: "#2E9E5B",
	down: "#D4504F",
	neutral: "#787F94",
} as const;

/**
 * Series identity, assigned in fixed order per panel and never cycled.
 *
 * Validated against the panel surface with the dataviz palette validator:
 * price pair and flow pair each pass all six checks on all pairs; the holding
 * stack passes on all pairs too.
 */
export const series = {
	/** Price panel. */
	vwap: "#A46BF2",
	valueFlow: "#C4832E",
	/** Flow panel. */
	proportion: "#3F8EF7",
	netProportion: "#C45C7C",
	/** Holding composition stack, bottom to top. */
	foreign: "#2A9FB0",
	localInstitutional: "#A08B22",
	localIndividual: "#8E7BE0",
} as const;

/**
 * Net-value profile bars, drawn in their own strip beside the price panel.
 *
 * Magnitude is bar length, so opacity is free to do one job only: separate a
 * significant bin from an ordinary one. Neither value washes the strip out,
 * because the strip does not sit on top of the candles.
 */
export const zone = {
	fill: { plain: 0.5, peak: 0.88 },
	peakEdge: "#C9A5FF",
} as const;

/** `#RRGGBB` plus an alpha, as `rgba()`. Chart fills need a value, not a class. */
export function withAlpha(hex: string, alpha: number): string {
	const value = Number.parseInt(hex.slice(1), 16);
	return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${Number(alpha.toFixed(3))})`;
}

export const typography = {
	/** UI text. */
	sans: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
	/** Every number, everywhere: tabular figures keep columns from jittering. */
	mono: '"JetBrains Mono", "SF Mono", "Cascadia Mono", ui-monospace, monospace',
} as const;

/** 4px base scale. */
export const space = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const;

/** Layout breakpoints, matching the three widths the workspace is built for. */
export const breakpoint = { mobile: 375, tablet: 768, desktop: 1280 } as const;
